import React, { useEffect, useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa";
import { claimPlayback, releasePlayback } from "./audioPlayback";

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";

  const whole = Math.floor(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
};

// A single sample. Play starts here, and audioPlayback pauses whoever was
// speaking before — including a player on another card.
const AudioPlayer = ({ src, title, subtitle }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onPlay = () => {
      claimPlayback(audio);
      setPlaying(true);
    };
    const onPause = () => {
      setPlaying(false);
      releasePlayback(audio);
    };
    const onTime = () => {
      setCurrent(audio.currentTime || 0);
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onMeta = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onEnded = () => {
      setPlaying(false);
      releasePlayback(audio);
    };
    const onError = () => setFailed(true);

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.pause();
      releasePlayback(audio);
    };
  }, [src]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio || failed) return;

    if (playing) {
      audio.pause();
    } else {
      const attempt = audio.play();
      if (attempt && typeof attempt.catch === "function") {
        attempt.catch(() => setFailed(true));
      }
    }
  };

  const seek = (event) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    audio.currentTime = Number(event.target.value);
    setCurrent(audio.currentTime);
  };

  const ratio = duration > 0 ? Math.min(1, current / duration) : 0;

  return (
    <div
      className={`flex items-center gap-[12px] rounded-2xl px-[14px] py-[12px] border transition-colors ${
        playing
          ? "border-purpleLight bg-silver"
          : "border-purpleLight/40 bg-white hover:border-purpleLight"
      }`}
    >
      <audio ref={audioRef} preload="metadata" src={src} />

      <button
        type="button"
        onClick={toggle}
        disabled={!src || failed}
        aria-label={playing ? "Pause" : "Play"}
        className={`shrink-0 w-[44px] h-[44px] rounded-full flex items-center justify-center text-white transition-all disabled:opacity-40 ${
          playing
            ? "bg-purple shadow-[0_8px_18px_-8px_rgb(var(--c-purple))]"
            : "bg-purpleLight hover:bg-purple"
        }`}
      >
        {playing ? (
          <FaPause className="w-[14px] h-[14px]" />
        ) : (
          <FaPlay className="w-[13px] h-[13px] ms-[2px]" />
        )}
      </button>

      <div className="min-w-0 flex-1 flex flex-col gap-[6px]">
        <div className="flex items-start justify-between gap-[8px]">
          <div className="min-w-0">
            <p className="text-purple font-semibold text-sm truncate">
              {title || subtitle}
            </p>
            {title && subtitle && (
              <p className="text-gray text-xs truncate">{subtitle}</p>
            )}
          </div>
          <span dir="ltr" className="shrink-0 text-xs text-gray tabular-nums">
            {failed ? "—" : `${formatTime(current)} / ${formatTime(duration)}`}
          </span>
        </div>

        <input
          type="range"
          min="0"
          max={duration || 0}
          step="0.1"
          value={duration ? current : 0}
          onChange={seek}
          disabled={!duration}
          aria-label="Seek"
          className="voice-seek w-full"
          style={{ "--seek": `${ratio * 100}%` }}
        />
      </div>
    </div>
  );
};

export default AudioPlayer;
